/* eslint-disable complexity */
/* eslint-disable max-statements */
/**
 * Parse info_idp cookie and extract customer data
 * This cookie is set by node-proxy cart endpoint with SameSite=None; Secure
 * and contains shopper authentication information for custom domains
 *
 * For delegation scenarios (CRM support accessing customer accounts):
 * - auth: "s2s" -> customer data is in the "s2s" object
 * - auth: "e2s" -> customer data might be in a nested object
 * - The "del" object contains the delegating user (CRM agent)
 *
 * @param {Object} req - Request object (server-side) or undefined (client-side)
 * @returns {Object|null} Parsed customer data or null if invalid/missing
 */
function parseInfoCustIdpCookie(req) {
  let cookieValue;

  // Server-side (from req.cookies)
  if (req && req.cookies) {
    cookieValue = req.cookies.info_idp;
  } else if (typeof document !== 'undefined') {
    // Client-side (from document.cookie)
    const cookies = document.cookie.split(';');
    const infoCookie = cookies.find(c => c.trim().startsWith('info_idp='));
    if (infoCookie) {
      cookieValue = infoCookie.split('=')[1];
    }
  }

  if (!cookieValue) {
    return null;
  }

  try {
    // Cookie might be URI encoded
    const decoded = decodeURIComponent(cookieValue);
    const data = JSON.parse(decoded);

    // For delegation auth types, customer data is in a nested object
    // s2s: server-to-server delegation (CRM accessing customer via API)
    // e2s: employee-to-shopper delegation
    // The actual customer data is in data.s2s or data.e2s respectively
    const customerData = getCustomerDataFromCookie(data);

    // Build display name from customer data with fallback to shopperId
    const displayName = buildDisplayName(customerData);

    return {
      loggedIn: data.auth !== 'none',
      shopperId: customerData.info_shopperId,
      name: customerData.firstname || customerData.username || customerData.info_shopperId,
      lastname: customerData.lastname,
      displayName,
      customerId: customerData.info_cid,
      privateLabelId: customerData.plid ? parseInt(customerData.plid, 10) : null,
      currency: customerData.currency,
      membershipLevel: customerData.mbr,
      authType: data.auth,
      customerType: customerData.ctype,
      username: customerData.username
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse info_idp cookie:', error);
    return null;
  }
}

/**
 * Extract customer data from cookie based on auth type
 * For delegation scenarios, data is nested in s2s/e2s objects
 *
 * @param {Object} data - Parsed cookie data
 * @returns {Object} Customer data object
 */
function getCustomerDataFromCookie(data) {
  // s2s delegation: customer data is in data.s2s
  if (data.auth === 's2s' && data.s2s) {
    return data.s2s;
  }

  // e2s delegation: customer data might be in data.e2s or similar
  if (data.auth === 'e2s' && data.e2s) {
    return data.e2s;
  }

  // e2s2s delegation: nested delegation scenario
  if (data.auth === 'e2s2s' && data.e2s2s) {
    return data.e2s2s;
  }

  // Default: use root level data (standard auth)
  return data;
}

/**
 * Build display name from customer data with fallback chain
 * Priority: firstname+lastname > username > shopperId
 *
 * @param {Object} customerData - Customer data object
 * @returns {string} Display name for the customer
 */
function buildDisplayName(customerData) {
  // Try firstname (with optional lastname)
  if (customerData.firstname) {
    return customerData.lastname
      ? `${customerData.firstname} ${customerData.lastname}`.trim()
      : customerData.firstname;
  }

  // Fallback to username
  if (customerData.username) {
    return customerData.username;
  }

  // Last resort: shopperId (ensures we always show something)
  if (customerData.info_shopperId) {
    return customerData.info_shopperId;
  }

  return '';
}

module.exports = { parseInfoCustIdpCookie };

